// src/pages/Account.js
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { user as userApi } from '../services/api';

function calcStrength(pw) {
  let score = 0;
  if (pw.length >= 8)                                score++;
  if (pw.length >= 12)                               score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw))         score++;
  if (/\d/.test(pw))                                 score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pw))            score++;
  const levels = [
    { label: '',          color: '' },
    { label: 'Weak',      color: '#ef4444' },
    { label: 'Fair',      color: '#f97316' },
    { label: 'Good',      color: '#eab308' },
    { label: 'Strong',    color: '#22c55e' },
    { label: 'Excellent', color: '#00ACC1' },
  ];
  return { ...levels[Math.min(score, 5)], score };
}

function Card({ title, sub, children }) {
  const theme = useTheme();
  return (
    <div className="rounded-2xl p-6 h-full"
      style={{
        backgroundColor: theme.card,
        boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)',
      }}>
      {title && (
        <div className="mb-5">
          <p className="text-sm font-semibold" style={{ color: theme.text }}>{title}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>{sub}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  const theme = useTheme();
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5" style={{ color: theme.text }}>{label}</label>
      {children}
    </div>
  );
}

export default function Account() {
  const theme                 = useTheme();
  const { user, refreshUser } = useAuth();

  const isDealer = !!user?.voucher_batch_id || user?.is_super_admin;
  const isPro    = user?.tier === 'pro';
  const proExpires = user?.pro_expires_at
    ? new Date(user.pro_expires_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  // Profile
  const [name,      setName]      = useState(user?.display_name || '');
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');
  const [saveError, setSaveError] = useState('');

  // Password
  const [curPw,       setCurPw]       = useState('');
  const [newPw,       setNewPw]       = useState('');
  const [showCurPw,   setShowCurPw]   = useState(false);
  const [showNewPw,   setShowNewPw]   = useState(false);
  const [pwSaving,    setPwSaving]    = useState(false);
  const [pwMsg,       setPwMsg]       = useState('');
  const [pwError,     setPwError]     = useState('');

  const strength = calcStrength(newPw);

  const inputStyle = {
    backgroundColor: theme.background,
    borderColor:     theme.border,
    color:           theme.text,
  };

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaveError(''); setSaveMsg('');
    setSaving(true);
    const res = await userApi.update({ display_name: name });
    setSaving(false);
    if (res.success) {
      await refreshUser();
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } else {
      setSaveError(res.error || 'Failed to save');
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError(''); setPwMsg('');
    if (newPw.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    setPwSaving(true);
    const res = await userApi.changePassword(curPw, newPw);
    setPwSaving(false);
    if (res.success) {
      setPwMsg('Password updated');
      setCurPw(''); setNewPw('');
      setTimeout(() => setPwMsg(''), 2500);
    } else {
      setPwError(res.error || 'Failed to update password');
    }
  }

  return (
    <div className="max-w-3xl mx-auto pb-10">
      <div className="flex justify-center mb-6">
        <span className="text-sm font-semibold px-5 py-2 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #00BCD4, #4CAF50)',
            color: '#fff',
          }}>
          Account
        </span>
      </div>

      {/* Two column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">

        {/* Profile */}
        <Card title="Profile" sub="Update your display name">
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <Field label="Display Name">
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                autoComplete="name" placeholder="Your name"
                className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors"
                style={inputStyle} />
            </Field>
            <Field label="Email">
              <input type="email" value={user?.email || ''} disabled
                className="w-full px-4 py-3 rounded-xl border text-sm opacity-50"
                style={inputStyle} />
            </Field>
            <div className="flex items-center gap-3 pt-1">
              <button type="submit" disabled={saving}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-brand disabled:opacity-60 transition-all hover:shadow-button">
                {saving ? 'Saving...' : 'Save'}
              </button>
              {saveMsg   && <span className="text-sm" style={{ color: '#16a34a' }}>{saveMsg}</span>}
              {saveError && <span className="text-sm" style={{ color: '#f87171' }}>{saveError}</span>}
            </div>
          </form>
        </Card>

        {/* Password — email accounts only */}
        {user?.auth_provider !== 'google' && (
          <Card title="Change Password">
            <form onSubmit={handleChangePassword} className="space-y-4">
              {pwError && (
                <div className="px-4 py-3 rounded-xl text-sm border"
                  style={{ backgroundColor: theme.isDark ? '#3b1111' : '#fef2f2', color: '#f87171', borderColor: '#f87171' }}>
                  {pwError}
                </div>
              )}
              <Field label="Current Password">
                <div className="relative">
                  <input type={showCurPw ? 'text' : 'password'} value={curPw} onChange={e => setCurPw(e.target.value)}
                    required autoComplete="current-password" placeholder="••••••••"
                    className="w-full px-4 py-3 pr-11 rounded-xl border text-sm outline-none transition-colors"
                    style={inputStyle} />
                  <button type="button" onClick={() => setShowCurPw(!showCurPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity"
                    style={{ color: theme.textMuted }}>
                    {showCurPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </Field>
              <Field label="New Password">
                <div className="relative">
                  <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
                    required autoComplete="new-password" placeholder="Min 8 characters"
                    className="w-full px-4 py-3 pr-11 rounded-xl border text-sm outline-none transition-colors"
                    style={inputStyle} />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity"
                    style={{ color: theme.textMuted }}>
                    {showNewPw ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {newPw.length > 0 && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: theme.border }}>
                      <div className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${(strength.score / 5) * 100}%`, backgroundColor: strength.color }} />
                    </div>
                    <span className="text-xs font-medium" style={{ color: strength.color }}>{strength.label}</span>
                  </div>
                )}
              </Field>
              <div className="flex items-center gap-3 pt-1">
                <button type="submit" disabled={pwSaving}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-brand disabled:opacity-60 transition-all hover:shadow-button">
                  {pwSaving ? 'Updating...' : 'Update Password'}
                </button>
                {pwMsg && <span className="text-sm" style={{ color: '#16a34a' }}>{pwMsg}</span>}
              </div>
            </form>
          </Card>
        )}

        {/* Subscription — regular users only */}
        {!isDealer && (
          <Card title="Subscription">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: theme.text }}>
                  {isPro ? 'Pro' : 'Free plan'}
                </p>
                {isPro && proExpires && (
                  <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>Expires {proExpires}</p>
                )}
                {!isPro && (
                  <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>Upgrade to unlock exports and more</p>
                )}
              </div>
              {!isPro && (
                <a href="/portal/upgrade"
                  className="text-xs font-semibold px-4 py-2 rounded-xl text-white transition-opacity hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
                  Upgrade
                </a>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}